import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { MAINAPIURL, APPBACKGROUND_COLOR } from '../appsettings.js';
import Footerdiv from "../layout/footer.js";
import Headerdiv from "../layout/header.js";
import {
  Layout,
  Typography,
  Form,
  Input,
  Select,
  Button,
  Table,
  Card,
  Row,
  Col,
  Space,
  Tag,
  theme,
  Popconfirm,
  Breadcrumb
} from 'antd';
import axios from 'axios';
import { AlertError, AlertSuccess } from '../usableclasses.js';

const { Option } = Select;
const { Content } = Layout;

const GrnList = () => {
  const navigate = useNavigate();
  const ApiURL = MAINAPIURL;
  const TOKEN = localStorage.getItem("AUTHCODE");

  const { token: { colorBgContainer, borderRadiusLG } } = theme.useToken();

  // form
  const [form] = Form.useForm();

  // states
  const [searchParams, setSearchParams] = useState({
    keyword: '',
    financialYear: '',
    year: '',
    month: '',
    purchaseOrder: '',
    status: '',
    sortField: '',
    sortMethod: ''
  });

  const [filteredData, setFilteredData] = useState([]);
  const [yearList, setYearList] = useState([]);
  const [financialYears, setFinancialYears] = useState([]);
  const [vendors, setVendors] = useState([]);

  const [loading, setLoading] = useState(false);
  const [loadingFinancialYears, setLoadingFinancialYears] = useState(false);
  const [loadingVendors, setLoadingVendors] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 5;
  const [totalRecords, setTotalRecords] = useState(0);

  // helper - stable date formatter
  const formatDate = useCallback((dateString) => {
    if (!dateString) return '';
    try {
      const d = new Date(dateString);
      return isNaN(d.getTime()) ? dateString : d.toLocaleDateString();
    } catch (e) {
      return dateString;
    }
  }, []);

  // status badge helper
  const getStatusBadge = useCallback((status) => {
    switch ((status || '').toUpperCase()) {
      case 'DRAFT':
        return <Tag color="orange">Draft</Tag>;
      case 'PENDING':
        return <Tag color="blue">Pending</Tag>;
      case 'APPROVED':
        return <Tag color="green">Approved</Tag>;
      case 'REJECTED':
        return <Tag color="red">Rejected</Tag>;
      case 'DELETED':
        return <Tag color="red">Deleted</Tag>;
      default:
        return <Tag color="default">{status}</Tag>;
    }
  }, []);

  // build year list once
  useEffect(() => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let i = 0; i < 10; i++) years.push((currentYear - i).toString());
    setYearList(years);

    // clear previous results on mount
    setFilteredData([]);
    setTotalRecords(0);
    setCurrentPage(1);
  }, []);

  // fetch financial years
  const fetchFinancialYears = useCallback(async () => {
    setLoadingFinancialYears(true);
    try {
      const res = await axios.get(`${ApiURL}/master/financeyear/dropdown`, {
        headers: { SystemAccessToken: TOKEN }
      });
      setFinancialYears(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      AlertError('Failed to load financial years');
      setFinancialYears([]);
    } finally {
      setLoadingFinancialYears(false);
    }
  }, [ApiURL, TOKEN]);

  // fetch vendors/purchase orders for GRN dropdown
  const fetchVendors = useCallback(async () => {
    setLoadingVendors(true);
    try {
      const res = await axios.get(`${ApiURL}/purchase/poforgrndropdown`, {
        headers: { SystemAccessToken: TOKEN }
      });
      const arr = Array.isArray(res.data) ? res.data.filter(item => item && item.label) : [];
      setVendors(arr);
    } catch (err) {
      AlertError('Failed to load vendors');
      setVendors([]);
    } finally {
      setLoadingVendors(false);
    }
  }, [ApiURL, TOKEN]);

  // handle filter - stable reference
  const handleFilter = useCallback(async (values = {}, pageNum = 1) => {
    setLoading(true);
    try {
      const requestParams = {
        keyword: values.keyword || '',
        finYear: values.financialYear || '',
        sysYear: values.year || '',
        sysMonth: values.month || '',
        poId: values.purchaseOrder || '',
        currentStatus: values.status || '',
        pageLength: rowsPerPage,
        pageNumber: pageNum,
        orderColumn: values.sortField || '',
        orderBy: values.sortMethod || ''
      };

      const res = await axios.post(`${ApiURL}/grn/grnlistfilter`, requestParams, {
        headers: { SystemAccessToken: TOKEN }
      });

      let records = [];
      let total = 0;

      if (res?.data) {
        if (Array.isArray(res.data.dataList)) {
          records = res.data.dataList;
          total = res.data.rowcount || records.length;
        } else if (Array.isArray(res.data)) {
          records = res.data;
          total = records.length;
        } else if (Array.isArray(res.data.records)) {
          records = res.data.records;
          total = res.data.totalRecords || records.length;
        } else if (Array.isArray(res.data.data)) {
          records = res.data.data;
          total = res.data.total || records.length;
        } else if (res.data.poId && typeof res.data === 'object') {
          records = [res.data];
          total = 1;
        } else {
          AlertError("Data Error");
          // if existing data present, keep it (don't clear)
          if (filteredData.length > 0) {
            return;
          }
        }
      }

      // keep previous data if API returned empty but we already have data
      if ((records.length === 0) && filteredData.length > 0) {
        return;
      }

      const processed = (records || []).map(record => ({
        grnId: record.grnId || '',
        poId: record.poId || '',
        grnReferance: record.grnReferance || '',
        poReferance: record.poReferance || '',
        finYear: record.finYear || '',
        sysYear: record.sysYear || '',
        sysMonth: record.sysMonth || '',
        createDate: record.createDate || '',
        vendorName: record.vendorName || '',
        vendorBillNo: record.vendorBillNo || '',
        totalCost: record.totalCost || '0.00',
        totalPaid: record.totalPaid || '0.00',
        totalPayable: record.totalPayable || '0.00',
        currentStage: record.currentStage || '',
        createdBy: record.createdBy || '',
        createdDate: record.createdDate || '',
        stockName: record.stockName || ''
      }));

      if (processed.length > 0) {
        setFilteredData(processed);
        setTotalRecords(total);
        setCurrentPage(pageNum);
        setSearchParams(values);
      } else {
        // if no results and no prior data, clear
        setFilteredData([]);
        setTotalRecords(0);
        setCurrentPage(1);
      }
    } catch (err) {
      AlertError('Failed to filter goods receives: ' + (err.message || 'Unknown error'));
      if (filteredData.length === 0) {
        setFilteredData([]);
        setTotalRecords(0);
        setCurrentPage(1);
      }
    } finally {
      setLoading(false);
    }
  }, [ApiURL, TOKEN, filteredData.length]);

  // handle clear
  const handleClear = useCallback(() => {
    form.resetFields();
    const defaultParams = {
      keyword: '',
      financialYear: '',
      year: '',
      month: '',
      purchaseOrder: '',
      status: '',
      sortField: '',
      sortMethod: ''
    };
    setSearchParams(defaultParams);
    handleFilter(defaultParams, 1);
  }, [form, handleFilter]);

  const handlePageChange = useCallback((page, pageSize) => {
    form.validateFields().then(values => {
      handleFilter(values, page);
    });
  }, [form, handleFilter]);

  // navigation handlers (stable)
  const handleViewPO = useCallback((po) => {
    navigate(`/grnview/${po.grnId}`);
  }, [navigate]);

  const handleEdit = useCallback((record) => {
    navigate(`/grnupdate/${record.grnId}`);
  }, [navigate]);

  const handleGotoPrint = useCallback((record) => {
    navigate(`/grnprint/${record.grnId}`);
  }, [navigate]);

  const handleDelete = useCallback(async (record) => {
    setLoading(true);
    try {
      const userId = localStorage.getItem("USERID") || "";

      const deletePayload = {
        PoId: record.poId,
        DeletedBy: userId
      };

      await axios.post(`${ApiURL}/grn/delete`, deletePayload, {
        headers: { SystemAccessToken: TOKEN }
      });

      AlertSuccess('goods receive deleted successfully');
      // refresh current page
      handleFilter(form.getFieldsValue(), currentPage);
    } catch (err) {
      AlertError('Failed to delete goods receive: ' + (err.response?.data?.message || err.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  }, [ApiURL, TOKEN, handleFilter, currentPage, form]);

  // columns WITHOUT sorters (memoized)
  const columns = useMemo(() => ([
    {
      title: 'GRN Reference',
      dataIndex: 'grnReferance',
      key: 'grnReferance',
    },
    {
      title: 'Create Date',
      dataIndex: 'createDate',
      key: 'createDate',
      render: (text) => formatDate(text),
    },
    {
      title: 'Stock Name',
      dataIndex: 'stockName',
      key: 'stockName',
    },
    {
      title: 'GRN Value',
      dataIndex: 'totalCost',
      key: 'totalCost',
      render: (value) => value ? `${parseFloat(value).toLocaleString()}` : '0.00',
    },
    {
      title: 'Vendor',
      dataIndex: 'vendorName',
      key: 'vendorName',
    },
    {
      title: 'Year',
      dataIndex: 'sysYear',
      key: 'sysYear',
    },
    {
      title: 'Month',
      dataIndex: 'sysMonth',
      key: 'sysMonth',
      render: (month) => {
        const monthNames = [
          'January','February','March','April','May','June',
          'July','August','September','October','November','December'
        ];
        if (month && !isNaN(month)) {
          const monthIndex = parseInt(month, 10) - 1;
          return monthNames[monthIndex] || month;
        }
        return month;
      }
    },
    {
      title: 'Vendor Bill No',
      dataIndex: 'vendorBillNo',
      key: 'vendorBillNo',
    },
    {
      title: 'Status',
      dataIndex: 'currentStage',
      key: 'currentStage',
      render: (status) => getStatusBadge(status),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => {
        const status = (record.currentStage || '').toUpperCase();
        return (
          <Space size="small">
            {status !== 'DRAFT' && (
              <>
                <Button type="primary" size="small" onClick={() => handleViewPO(record)}>View</Button>
                <Button color='purple' type='primary' variant='solid' size="small" onClick={() => handleGotoPrint(record)}>Print</Button>
              </>
            )}

            {status === 'DRAFT' && (
              <>
                <Button
                  type="default"
                  size="small"
                  color='yellow'
                  variant='outlined'
                  onClick={() => handleEdit(record)}
                >
                  Edit
                </Button>
                <Popconfirm
                  title="Are you sure you want to delete this goods receive?"
                  onConfirm={() => handleDelete(record)}
                  okText="Yes"
                  cancelText="No"
                >
                  <Button danger size="small">Delete</Button>
                </Popconfirm>
              </>
            )}

            {status === 'PENDING' && (
              <Popconfirm
                title="Are you sure you want to delete this goods receive?"
                onConfirm={() => handleDelete(record)}
                okText="Yes"
                cancelText="No"
              >
                <Button danger size="small">Delete</Button>
              </Popconfirm>
            )}
          </Space>
        );
      }
    }
  ]), [formatDate, getStatusBadge, handleViewPO, handleGotoPrint, handleEdit, handleDelete]);

  // pagination config memoized
  const paginationConfig = useMemo(() => ({
    current: currentPage,
    pageSize: rowsPerPage,
    total: totalRecords,
    onChange: handlePageChange,
    showSizeChanger: false,
    showTotal: (total, range) => `Showing ${range[0]}-${range[1]} of ${total} items`
  }), [currentPage, totalRecords, handlePageChange]);

  // initial data load
  useEffect(() => {
    fetchFinancialYears();
    fetchVendors();
    // initial filter using defaults
    const defaultParams = {
      keyword: '',
      financialYear: '',
      year: '',
      month: '',
      purchaseOrder: '',
      status: '',
      sortField: '',
      sortMethod: ''
    };
    handleFilter(defaultParams, 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // run once on mount

  return (
    <Layout style={{ backgroundColor: APPBACKGROUND_COLOR }}>
      <Headerdiv />
      <Content style={{ padding: '10px' }}>
        <Breadcrumb style={{ margin: "16px 0" }}>
          <Breadcrumb.Item>Goods Receive List</Breadcrumb.Item>
        </Breadcrumb>

        <div style={{ padding: 20, minHeight: '65vh', background: colorBgContainer, borderRadius: borderRadiusLG }}>
          <Space direction="vertical" style={{ width: "100%", padding: "15px" }}>
            <Row gutter={[16, 16]}>
              <Col span={24}>
                <Card title="Search goods receives" style={{ marginBottom: 16 }}>
                  <Form
                    form={form}
                    name="search_form"
                    layout="vertical"
                    onFinish={handleFilter}
                    initialValues={searchParams}
                  >
                    <Row gutter={[16, 8]}>
                      <Col xs={24} sm={12} md={4}>
                        <Form.Item name="keyword" label="Search Keyword">
                          <Input placeholder="Search..." />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12} md={5}>
                        <Form.Item name="financialYear" label="Financial Year">
                          <Select
                            placeholder="Select Financial Year"
                            allowClear
                            loading={loadingFinancialYears}
                          >
                            {financialYears.map(year => (
                              <Option key={year.id} value={year.name}>{year.name}</Option>
                            ))}
                          </Select>
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12} md={5}>
                        <Form.Item name="year" label="Year">
                          <Select placeholder="Select Year" allowClear>
                            {yearList.map((y) => (
                              <Option key={y} value={y}>{y}</Option>
                            ))}
                          </Select>
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12} md={5}>
                        <Form.Item name="month" label="Month">
                          <Select placeholder="Select Month" allowClear>
                            <Option value="01">January</Option>
                            <Option value="02">February</Option>
                            <Option value="03">March</Option>
                            <Option value="04">April</Option>
                            <Option value="05">May</Option>
                            <Option value="06">June</Option>
                            <Option value="07">July</Option>
                            <Option value="08">August</Option>
                            <Option value="09">September</Option>
                            <Option value="10">October</Option>
                            <Option value="11">November</Option>
                            <Option value="12">December</Option>
                          </Select>
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12} md={5}>
                        <Form.Item name="status" label="Status">
                          <Select placeholder="Select Status" allowClear>
                            <Option value="DRAFT">Draft</Option>
                            <Option value="PENDING">Pending</Option>
                            <Option value="APPROVED">Approved</Option>
                            <Option value="REJECTED">Rejected</Option>
                            <Option value="DELETED">Deleted</Option>
                          </Select>
                        </Form.Item>
                      </Col>
                    </Row>

                    <Row gutter={[16, 8]}>
                      <Col xs={24} sm={12} md={6}>
                        <Form.Item name="purchaseOrder" label="Purchase Order">
                          <Select
                            placeholder="Select purchaseOrder"
                            allowClear
                            loading={loadingVendors}
                          >
                            {vendors.map((purchaseOrder, index) => (
                              <Option key={purchaseOrder.id || index} value={purchaseOrder.id || ''}>
                                {purchaseOrder.label || 'Unknown purchaseOrder'}
                              </Option>
                            ))}
                          </Select>
                        </Form.Item>
                      </Col>

                      <Col xs={24} sm={12} md={6}>
                        <Form.Item name="sortField" label="Sort By">
                          <Select placeholder="Select Field">
                            <Option value="REFERANCE">PO Reference</Option>
                            <Option value="CREATED_DATE">Create Date</Option>
                            <Option value="FINANCE_YEAR">Finance Year</Option>
                            <Option value="COST">Total Cost</Option>
                            <Option value="PAID_AMOUNT">Paid Amount</Option>
                          </Select>
                        </Form.Item>
                      </Col>

                      <Col xs={24} sm={12} md={6}>
                        <Form.Item name="sortMethod" label="Sort Order">
                          <Select placeholder="Select Order">
                            <Option value="ASC">Ascending</Option>
                            <Option value="DESC">Descending</Option>
                          </Select>
                        </Form.Item>
                      </Col>

                      <Col xs={24} sm={12} md={6} style={{ display: 'flex', alignItems: 'center' }}>
                        <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
                          <Button type="primary" htmlType="submit">Filter</Button>
                          <Button onClick={handleClear}>Clear</Button>
                        </Space>
                      </Col>
                    </Row>
                  </Form>
                </Card>

                <Card title="Goods Receive List">
                  <div style={{ overflowX: 'auto' }}>
                    <Table
                      columns={columns}
                      dataSource={filteredData}
                      pagination={paginationConfig}
                      rowKey="grnId"
                      size="middle"
                      loading={loading}
                      scroll={{ x: 'max-content' }}
                      locale={{
                        emptyText: loading ? 'Loading...' : (<div><p>No data found</p></div>)
                      }}
                    />
                  </div>
                </Card>
              </Col>
            </Row>
          </Space>
        </div>
      </Content>
      <Footerdiv />
    </Layout>
  );
};

export default GrnList;
